import { useEffect, useState } from "react";
import { Search, Clock, Users, ArrowRight, FileText } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/contexts/auth.context";
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	CardAction,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import KorrekturOverviewService from "./korrekturOverview.service";
import type { AvailableKlausurDTO } from "./korrekturOverview.types";
import { Button } from "@/components/ui/button";
import StatsOverview from "./components/statsOverview.component";

// Enhanced status configuration for better UX
const getExamStatus = (korrekturStepsForUser: number) => {
	// Determine status based on available correction steps
	if (korrekturStepsForUser > 0) {
		return {
			type: "available",
			label: "Verfügbar",
			color: "bg-green-100 text-green-800 border-green-200",
			icon: "✓",
		};
	}
	return {
		type: "finished",
		label: "Beendet",
		color: "bg-blue-100 text-blue-800 border-blue-200",
		icon: "👁",
	};
};

const KlausurCard = ({
	klausur,
	onClick,
}: {
	klausur: AvailableKlausurDTO;
	onClick: () => void;
}) => {
	const status = getExamStatus(klausur.korrekturStepsForUser);
	// Use real exam metadata
	const korrekturenCount = klausur.korrekturStepsForUser;
	const dueDate = klausur.deadline ? new Date(klausur.deadline) : null;

	return (
		<Card
			className="group relative transition-all duration-200 hover:shadow-lg hover:scale-[1.02] cursor-pointer border border-gray-200 hover:border-gray-300 bg-white"
			onClick={onClick}
		>
			<CardHeader className="pb-4 flex flex-col items-start justify-between">
				<div className="flex flex-col items-start justify-between">
					<CardAction>
						<Badge
							variant="outline"
							className={`${status.color} text-xs font-medium border py-1`}
						>
							{status.label}
						</Badge>
					</CardAction>
				</div>
				<CardTitle className="text-lg font-semibold text-gray-900 group-hover:text-primary transition-colors line-clamp-2">
					{klausur.title}
				</CardTitle>
			</CardHeader>
			<CardContent className="pt-0">
				<div className="space-y-3">
					{/* Exam metadata */}
					<div className="flex items-center justify-between text-sm">
						<div className="flex items-center gap-1 text-gray-600">
							<Users className="w-4 h-4" />
							<span>{korrekturenCount} Korrekturen</span>
						</div>
						<div className="flex items-center gap-1 text-gray-600">
							<Clock className="w-4 h-4" />
							<span>
								{dueDate
									? dueDate.toLocaleDateString("de-DE", {
											month: "short",
											day: "numeric",
										})
									: "Kein Termin"}
							</span>
						</div>
					</div>

					{/* Progress indicator */}
					<div className="space-y-2">
						<div className="flex justify-between text-xs text-gray-600">
							<span>Fortschritt</span>
							<span>
								{klausur.completedKorrekturStepsForUser}/
								{klausur.korrekturStepsForUser}
							</span>
						</div>
						<div className="w-full bg-gray-200 rounded-full h-2">
							<div
								className="bg-primary h-2 rounded-full transition-all duration-300"
								style={{
									width: `${(() => {
										if (klausur.korrekturStepsForUser <= 0) return 0;
										const percentage =
											(klausur.completedKorrekturStepsForUser /
												klausur.korrekturStepsForUser) *
											100;
										return Math.min(Math.max(percentage, 0), 100);
									})()}%`,
								}}
							/>
						</div>
					</div>

					{/* Call to action */}
					<div className="flex items-center justify-between pt-2 border-t border-gray-100">
						<span className="text-sm text-gray-600">Korrekturen anzeigen</span>
						<ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-primary transition-colors" />
					</div>
				</div>
			</CardContent>
		</Card>
	);
};

/**
 * Dashboard page for the korrektur dashboard
 */
export default function DashboardPage() {
	const { user } = useAuth();
	const navigate = useNavigate();
	const [klausuren, setKlausuren] = useState<AvailableKlausurDTO[]>([]);
	const [loading, setLoading] = useState(true);
	const [searchTerm, setSearchTerm] = useState("");
	const [error, setError] = useState<string | null>(null);
	const korrekturOverviewService = KorrekturOverviewService.getInstance();
	const { currentOrganization } = useAuth();

	useEffect(() => {
		/**
		 * Get available klausuren for the current organization
		 */
		const fetchKlausuren = async () => {
			try {
				setLoading(true);
				korrekturOverviewService.createKorrekturen();
				const data = await korrekturOverviewService.getAvailableKlausuren(
					currentOrganization.organizationId,
				);
				setKlausuren(data);
			} catch (err) {
				setError("Fehler beim Laden der Klausuren");
				console.error("Error fetching klausuren:", err);
			} finally {
				setLoading(false);
			}
		};

		if (user) {
			fetchKlausuren();
		}
	}, [user, korrekturOverviewService, currentOrganization.organizationId]);

	const handleKlausurSelect = (klausurId: string) => {
		navigate(`/korrektur-overview/${klausurId}`);
	};

	if (loading) {
		return (
			<div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50">
				<div className="p-6 max-w-7xl mx-auto">
					{/* Loading Header */}
					<div className="mb-8">
						<div className="flex items-center gap-3 mb-4">
							<div className="p-3 bg-blue-600 rounded-xl animate-pulse">
								<FileText className="w-6 h-6 text-white" />
							</div>
							<div>
								<div className="h-8 w-64 bg-gray-200 rounded animate-pulse mb-2" />
								<div className="h-4 w-96 bg-gray-100 rounded animate-pulse" />
							</div>
						</div>
					</div>

					{/* Loading Cards */}
					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
						{Array.from({ length: 8 }, (_, i) => (
							<Card
								key={`loading-card-${Date.now()}-${i}`}
								className="animate-pulse"
							>
								<CardHeader className="pb-4">
									<div className="flex items-start justify-between mb-3">
										<div className="flex items-center gap-2">
											<div className="w-10 h-10 bg-gray-200 rounded-lg" />
											<div className="w-16 h-5 bg-gray-200 rounded" />
										</div>
										<div className="w-20 h-6 bg-gray-200 rounded-full" />
									</div>
									<div className="h-6 w-full bg-gray-200 rounded mb-2" />
									<div className="h-4 w-2/3 bg-gray-100 rounded" />
								</CardHeader>
								<CardContent className="pt-0">
									<div className="space-y-3">
										<div className="flex justify-between">
											<div className="h-4 w-24 bg-gray-200 rounded" />
											<div className="h-4 w-16 bg-gray-200 rounded" />
										</div>
										<div className="space-y-2">
											<div className="flex justify-between">
												<div className="h-3 w-16 bg-gray-100 rounded" />
												<div className="h-3 w-8 bg-gray-100 rounded" />
											</div>
											<div className="w-full h-2 bg-gray-200 rounded" />
										</div>
									</div>
								</CardContent>
							</Card>
						))}
					</div>
				</div>
			</div>
		);
	}

	if (error) {
		return (
			<div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50">
				<div className="p-6 max-w-7xl mx-auto">
					<div className="text-center py-16">
						<div className="max-w-md mx-auto">
							<div className="p-4 bg-red-100 rounded-full w-20 h-20 mx-auto mb-4 flex items-center justify-center">
								<FileText className="w-8 h-8 text-red-600" />
							</div>
							<h3 className="text-lg font-semibold text-gray-900 mb-2">
								Fehler beim Laden
							</h3>
							<p className="text-gray-600 mb-6">{error}</p>
							<Button
								onClick={() => window.location.reload()}
								className="px-6 py-2 text-white rounded-lg hover:bg-primary transition-colors"
							>
								Seite neu laden
							</Button>
						</div>
					</div>
				</div>
			</div>
		);
	}

	// Filter klausuren based on search term
	const filteredKlausuren = klausuren.filter(
		(klausur) =>
			klausur.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
			klausur.id.toLowerCase().includes(searchTerm.toLowerCase()),
	);
	const completedKorrekturen = klausuren.reduce(
		(acc, klausur) => acc + klausur.completedKorrekturStepsForUser,
		0,
	);
	const allKorrekturen = klausuren.reduce(
		(acc, klausur) => acc + klausur.korrekturStepsForUser,
		0,
	);
	const availableKorrekturen = klausuren.reduce(
		(acc, klausur) => acc + klausur.availableKorrekturStepsForUser,
		0,
	);

	const offeneKorrekturen = availableKorrekturen;

	return (
		<div className="min-h-screen ">
			<div className="p-6 max-w-7xl mx-auto">
				{/* Enhanced Header */}
				<div className="mb-8">
					<div className="flex items-center gap-3 mb-4">
						<div className="p-3 bg-blue-600 rounded-xl">
							<FileText className="w-6 h-6 text-white" />
						</div>
						<div>
							<h1 className="text-3xl font-bold text-gray-900">
								Klausurübersicht
							</h1>
							<p className="text-gray-600 mt-1">
								Wähle eine Klausur aus, um die Korrekturen anzuzeigen und zu
								verwalten
							</p>
						</div>
					</div>

					{/* Stats Overview */}
					<StatsOverview
						title="Korrektur-Statistik"
						items={[
							{
								label: "Offen",
								value: offeneKorrekturen,
								valueClassName: "text-amber-600",
							},
							{
								label: "Abgeschlossen",
								value: completedKorrekturen,
								valueClassName: "text-emerald-600",
							},
							{
								label: "Gesamt",
								value: allKorrekturen,
								valueClassName: "text-blue-600",
							},
						]}
					/>
				</div>

				{/* Enhanced Search */}
				<div className="mb-8">
					<div className="relative max-w-md">
						<Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
						<Input
							type="text"
							placeholder="Klausuren durchsuchen..."
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
							className="pl-12 pr-4 py-3 text-base border-gray-300 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent shadow-sm"
						/>
						{searchTerm && (
							<Button
								onClick={() => setSearchTerm("")}
								variant="ghost"
								className="absolute right-2 top-1/2 transform -translate-y-1/2 "
							>
								✕
							</Button>
						)}
					</div>
					{searchTerm && (
						<p className="mt-2 text-sm text-gray-600">
							{filteredKlausuren.length} von {klausuren.length} Klausuren
							gefunden
						</p>
					)}
				</div>

				{/* Available Klausuren */}
				{filteredKlausuren.length > 0 ? (
					<div>
						<div className="flex items-center justify-between mb-6">
							<h2 className="text-xl font-semibold text-gray-900">
								Verfügbare Klausuren
							</h2>
							<div className="text-sm text-gray-600">
								{filteredKlausuren.length} Klausur
								{filteredKlausuren.length !== 1 ? "en" : ""}
							</div>
						</div>
						<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
							{filteredKlausuren.map((klausur) => (
								<KlausurCard
									key={klausur.id}
									klausur={klausur}
									onClick={() => handleKlausurSelect(klausur.id)}
								/>
							))}
						</div>
					</div>
				) : (
					<div className="text-center py-16">
						<div className="max-w-md mx-auto w-full">
							<div className="p-4 bg-gray-100 rounded-full w-20 h-20 mx-auto mb-4 flex items-center justify-center">
								<Search className="w-8 h-8 text-gray-400" />
							</div>
							<h3 className="text-lg font-semibold text-gray-900 mb-2">
								{searchTerm
									? "Keine Klausuren gefunden"
									: "Zurzeit ist dir keine Klausur zugewiesen oder du hast bereits alle Korrekturen abgeschlossen."}
							</h3>
							<p className="text-gray-600">
								{searchTerm
									? "Versuche einen anderen Suchbegriff oder lösche den Filter."
									: "Sobald der Organisations-Admin neue Zuweisungen vornimmt, werden sie hier angezeigt."}
							</p>
							{searchTerm && (
								<Button onClick={() => setSearchTerm("")}>
									Filter zurücksetzen
								</Button>
							)}
						</div>
					</div>
				)}
			</div>
		</div>
	);
}
